/**
 * ธีม "พื้นหลังไหล" (Ground) — logic ล้วน ไม่แตะ DOM (ทดสอบด้วย node --test)
 *
 * แอดมินเลือกที่ admin.srikanett.com › จัดการระบบ › ธีมหน้าลูกค้า › Ground (พื้นหลังไหล)
 * Firestore `publicIntegrations/ordercusUi`: theme='glass', palette='ground', ground={ preset, custom }
 *   (OrderCus เวอร์ชันเก่าที่ไม่รู้จัก palette 'ground' จะเห็น glass ม่วง-ทองเดิม ไม่หลุดเป็น classic)
 * ถ้าแก้ชื่อฟิลด์/ค่า/preset ต้องแก้ฝั่งแอดมินพร้อมกัน: components/AdminIslands/features/ordercusTheme/groundLogic.ts
 *
 * พื้นหลัง = WebGL shader เส้นแสงไหลช้า (สูตรจาก HyperFrames) คลุมม่านสีทึบ
 */

export type GroundPreset = 'hf' | 'temple' | 'velvet' | 'champagne' | 'custom'
export type GroundLineMode = 'rainbow' | 'mono'
export type GroundTone = 'light' | 'dark'

export interface GroundCustom {
  base: string
  mode: GroundLineMode
  tint: string
  veil: string
  /** ความทึบของม่าน 0–0.95 */
  alpha: number
}

export interface GroundConfig {
  preset: GroundPreset
  custom: GroundCustom
}

/** ค่าที่ใช้วาดจริง (resolve จาก preset/custom แล้ว) */
export interface GroundResolved extends GroundCustom {
  preset: GroundPreset
  tone: GroundTone
  /** สีทอง/สีเน้นของธีม (ราคา ปุ่ม) */
  gold: string
}

export const GROUND_PRESETS: readonly GroundPreset[] = ['hf', 'temple', 'velvet', 'champagne', 'custom']
export const GROUND_STORAGE_KEY = 'oc-ground'
/** "dark|#030202" — โทน+สีพื้นล่าสุด ให้ inline script ใน index.html (ES5) ตั้ง data-scheme/สีพื้นก่อนวาดหน้า */
export const GROUND_BOOT_KEY = 'oc-ground-boot'
export const GROUND_ALPHA_MAX = 0.95

export const GROUND_CUSTOM_DEFAULT: GroundCustom = { base: '#ffffff', mode: 'rainbow', tint: '#c9a24e', veil: '#f7ecd3', alpha: 0.7 }
export const GROUND_DEFAULT: GroundConfig = { preset: 'hf', custom: { ...GROUND_CUSTOM_DEFAULT } }

type PresetDef = Omit<GroundResolved, 'preset'>

/** รหัสสีของธีมศรีคเนศ กำหนดจากชื่อในโจทย์ (Antique Gold / Champagne Gold / Deep Velvet) — แก้ได้ที่นี่ที่เดียว */
const PRESET_DEFS: Record<Exclude<GroundPreset, 'custom'>, PresetDef> = {
  hf: { base: '#ffffff', mode: 'rainbow', tint: '#c9a24e', veil: '#f7ecd3', alpha: 0.8, tone: 'light', gold: '#8a5a12' },
  temple: { base: '#030202', mode: 'mono', tint: '#b8923a', veil: '#000000', alpha: 0.62, tone: 'dark', gold: '#d4af5a' },
  velvet: { base: '#25070d', mode: 'mono', tint: '#e7cf9a', veil: '#2a0810', alpha: 0.58, tone: 'dark', gold: '#e7cf9a' },
  champagne: { base: '#ffffff', mode: 'mono', tint: '#c9a24e', veil: '#ecd9a8', alpha: 0.72, tone: 'light', gold: '#7d5410' },
}

const HEX = /^#[0-9a-f]{6}$/i

export function parseGroundPreset(v: unknown): GroundPreset | null {
  return typeof v === 'string' && (GROUND_PRESETS as readonly string[]).includes(v) ? (v as GroundPreset) : null
}

function hex(v: unknown, fallback: string): string {
  return typeof v === 'string' && HEX.test(v) ? v.toLowerCase() : fallback
}

export function normalizeGroundCustom(raw: unknown): GroundCustom {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const a = typeof o.alpha === 'number' && Number.isFinite(o.alpha) ? o.alpha : GROUND_CUSTOM_DEFAULT.alpha
  return {
    base: hex(o.base, GROUND_CUSTOM_DEFAULT.base),
    mode: o.mode === 'mono' ? 'mono' : 'rainbow',
    tint: hex(o.tint, GROUND_CUSTOM_DEFAULT.tint),
    veil: hex(o.veil, GROUND_CUSTOM_DEFAULT.veil),
    alpha: Math.min(GROUND_ALPHA_MAX, Math.max(0, a)),
  }
}

export function normalizeGroundConfig(raw: unknown): GroundConfig {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return { preset: parseGroundPreset(o.preset) ?? 'hf', custom: normalizeGroundCustom(o.custom) }
}

function channels(h: string): [number, number, number] {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]
}

/** ความสว่างสัมพัทธ์ 0–1 ของสีที่ผู้ใช้เห็นจริง (พื้น + ม่านทับ) */
export function effectiveLuminance(c: Pick<GroundCustom, 'base' | 'veil' | 'alpha'>): number {
  const b = channels(c.base)
  const v = channels(c.veil)
  const mix = (i: 0 | 1 | 2) => b[i] * (1 - c.alpha) + v[i] * c.alpha
  return (0.2126 * mix(0) + 0.7152 * mix(1) + 0.0722 * mix(2)) / 255
}

/** ธีมกำหนดเอง: เลือกโทนตัวหนังสือจากความสว่างของพื้น+ม่าน · เลือกสีทองให้อ่านได้บนโทนนั้น */
export function toneForCustom(c: GroundCustom): GroundTone {
  return effectiveLuminance(c) < 0.5 ? 'dark' : 'light'
}

export function resolveGround(config: GroundConfig): GroundResolved {
  if (config.preset !== 'custom') return { preset: config.preset, ...PRESET_DEFS[config.preset] }
  const c = config.custom
  const tone = toneForCustom(c)
  return { preset: 'custom', ...c, tone, gold: tone === 'dark' ? '#e8c26a' : '#8a5a12' }
}

/** อ่าน ground จาก response Firestore REST (fields.ground.mapValue.fields.*) — ไม่มี/ผิดรูป = hf */
export function groundFromRestDoc(doc: unknown): GroundConfig {
  type F = Record<string, { stringValue?: unknown; doubleValue?: unknown; integerValue?: unknown; mapValue?: { fields?: Record<string, F[string]> } }>
  const g = (doc as { fields?: { ground?: { mapValue?: { fields?: F } } } } | null)?.fields?.ground?.mapValue?.fields
  if (!g) return normalizeGroundConfig(null)
  const c = g.custom?.mapValue?.fields
  const num = (x: F[string] | undefined) =>
    x?.doubleValue !== undefined ? Number(x.doubleValue) : x?.integerValue !== undefined ? Number(x.integerValue) : undefined
  return normalizeGroundConfig({
    preset: g.preset?.stringValue,
    custom: c && {
      base: c.base?.stringValue,
      mode: c.mode?.stringValue,
      tint: c.tint?.stringValue,
      veil: c.veil?.stringValue,
      alpha: num(c.alpha),
    },
  })
}

/** ค่าใน localStorage (JSON) → GroundConfig · พัง/ไม่มี = hf */
export function parseStoredGround(raw: unknown): GroundConfig {
  if (typeof raw !== 'string') return normalizeGroundConfig(null)
  try {
    return normalizeGroundConfig(JSON.parse(raw))
  } catch {
    return normalizeGroundConfig(null)
  }
}

export function groundEqual(a: GroundConfig, b: GroundConfig): boolean {
  return a.preset === b.preset && JSON.stringify(a.custom) === JSON.stringify(b.custom)
}

/** rgba() สำหรับม่านทับ */
export function veilCss(r: Pick<GroundResolved, 'veil' | 'alpha'>): string {
  const [rr, gg, bb] = channels(r.veil)
  return `rgba(${rr}, ${gg}, ${bb}, ${r.alpha})`
}

/** ค่าสี 0–1 สำหรับส่งเข้า uniform ของ shader */
export function rgb01(h: string): [number, number, number] {
  const [r, g, b] = channels(h)
  return [r / 255, g / 255, b / 255]
}
